/**
 * Ce que le tunnel fait des horaires servis par l'api, sans I/O ni React.
 *
 * `GET /v2/public/shops/{id}/availability` rend `{ duration_min, slots }`, où
 * `slots` est un tableau de CHAÎNES ISO 8601 Zulu : les heures de DÉBUT libres,
 * au pas de 30 minutes (`AvailabilityService::STEP_MINUTES`). Tout ce qui est
 * servi est proposable par construction ; ce qui n'est pas servi peut être pris
 * OU fermé, et rien ne permet de distinguer les deux tant que l'api n'expose
 * pas les horaires d'ouverture (FIXERLIGHT-053). D'où l'absence de « Complet »
 * dans ce lot : on n'affiche que ce qui est libre.
 *
 * Tout se lit à l'heure de PARIS, jamais dans le fuseau de l'appareil : le
 * rendez-vous a lieu chez le client, en France. Un téléphone resté réglé sur
 * un autre fuseau ne doit décaler ni les heures ni les jours. Un créneau à
 * 23:00 à Paris en été est un `21:00Z` qui appartient au jour précédent en UTC.
 */

export const FUSEAU = 'Europe/Paris';

const formats = {};
const format = (locale, options) => {
  const cle = locale + JSON.stringify(options);
  if (!formats[cle]) {
    formats[cle] = new Intl.DateTimeFormat(locale, {
      timeZone: FUSEAU,
      ...options,
    });
  }
  return formats[cle];
};
const date = (valeur) => (valeur instanceof Date ? valeur : new Date(valeur));

/** `YYYY-MM-DD` d'une date, à l'heure de Paris. */
export const cleJour = (valeur) =>
  // en-CA écrit les dates en AAAA-MM-JJ.
  format('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    date(valeur),
  );

/** La clé du jour `n` jours après `cle`, en jours de calendrier. */
export const jourSuivant = (cle, n = 1) => {
  const [a, m, j] = cle.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, j + n)).toISOString().slice(0, 10);
};

/** « 09:30 ». */
export const heure = (iso) =>
  format('fr-FR', { hour: '2-digit', minute: '2-digit' }).format(date(iso));

/** L'heure de Paris, de 0 à 23. */
const heureDuJour = (iso) =>
  Number(
    format('en-GB', { hour: '2-digit', hourCycle: 'h23' }).format(date(iso)),
  );

/** « mardi 22 septembre ». */
export const jourLong = (iso) =>
  format('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).format(
    date(iso),
  );

/** La capitale au premier mot seulement : « Mardi 22 septembre ». */
export const majuscule = (t) =>
  t ? t.charAt(0).toUpperCase() + t.slice(1) : t;

/** « mardi 22 septembre à 14:00 ». */
export const horaireLong = (iso) => `${jourLong(iso)} à ${heure(iso)}`;

/** « mardi 22 à 14:00 », pour un bouton. */
export const horaireCourt = (iso) =>
  `${format('fr-FR', { weekday: 'long', day: 'numeric' }).format(
    date(iso),
  )} à ${heure(iso)}`;

/**
 * Le libellé d'une pastille de jour, comme dans l'app mobile
 * (`rj-mobile/src/features/booking/utils.ts`, `formatDateFR`) :
 * « Aujourd'hui », « Demain », sinon « Mer. 23 septembre ».
 */
export function libellePastille(iso, maintenant = new Date()) {
  const ecart = ecartJours(iso, maintenant);
  if (ecart === 0) return 'Aujourd’hui';
  if (ecart === 1) return 'Demain';
  // « mer. » → « Mer. », comme l'app mobile.
  const jour = majuscule(
    format('fr-FR', { weekday: 'short' }).format(date(iso)),
  );
  return `${jour} ${format('fr-FR', { day: 'numeric', month: 'long' }).format(
    date(iso),
  )}`;
}

/**
 * 0 si le créneau tombe aujourd'hui (heure de Paris), 1 demain, 2 au-delà
 * (ou avant).
 */
export function ecartJours(iso, maintenant = new Date()) {
  const cle = cleJour(iso);
  const aujourdhui = cleJour(maintenant);
  if (cle === aujourdhui) return 0;
  if (cle === jourSuivant(aujourdhui)) return 1;
  return 2;
}

/** Les heures libres groupées par jour local, dans l'ordre chronologique. */
export function parJour(slots) {
  const jours = new Map();
  [...(slots || [])]
    .sort((a, b) => a.localeCompare(b))
    .forEach((iso) => {
      const cle = cleJour(iso);
      if (!jours.has(cle)) jours.set(cle, []);
      jours.get(cle).push(iso);
    });
  return [...jours.entries()].map(([cle, creneaux]) => ({ cle, creneaux }));
}

/**
 * Les blocs d'une heure du mode « demande à confirmer », comme dans l'app
 * mobile (« 09h-10h »). Un bloc existe dès qu'une heure de début libre tombe
 * dans son heure ; c'est cette PREMIÈRE heure libre qui part dans la demande,
 * parce que l'api refuse tout créneau qui n'est pas réellement proposable
 * (`AvailabilityService::rejectNotOffered`) : un « 10:00 » inventé pour un
 * bloc dont le premier départ libre est 10:30 serait refusé en 422.
 */
export function blocsHoraires(creneauxDuJour) {
  const blocs = new Map();
  creneauxDuJour.forEach((iso) => {
    const h = heureDuJour(iso);
    if (!blocs.has(h)) blocs.set(h, iso);
  });
  return [...blocs.entries()]
    .sort(([a], [b]) => a - b)
    .map(([h, iso]) => ({
      heure: h,
      iso,
      libelle: `${String(h).padStart(2, '0')}h-${String(h + 1).padStart(
        2,
        '0',
      )}h`,
    }));
}

/**
 * Les jours groupant heures libres ET heures complètes (FIXERLIGHT-053/070) :
 * un jour apparaît dès qu'il porte l'une des deux, entièrement grisé s'il ne
 * porte que du complet (jour fermé par une exception totale, ou entièrement
 * pris — `docs/specs/fixerlight-053-creneaux-complets.md` § 3). `fullSlots`
 * absent ou vide se comporte exactement comme avant FIXERLIGHT-053 : aucun
 * jour complet, `creneauxComplets` toujours vide.
 */
export function joursCombines(slots, fullSlots) {
  const jours = new Map();
  const ajoute = (liste, champ) => {
    (liste || []).forEach((iso) => {
      const cle = cleJour(iso);
      if (!jours.has(cle))
        jours.set(cle, { creneaux: [], creneauxComplets: [] });
      jours.get(cle)[champ].push(iso);
    });
  };
  ajoute(slots, 'creneaux');
  ajoute(fullSlots, 'creneauxComplets');
  return [...jours.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cle, { creneaux, creneauxComplets }]) => ({
      cle,
      creneaux: [...creneaux].sort((a, b) => a.localeCompare(b)),
      creneauxComplets: [...creneauxComplets].sort((a, b) =>
        a.localeCompare(b),
      ),
    }));
}

/**
 * Toutes les heures d'un jour de `joursCombines`, libres et complètes
 * mélangées dans l'ordre chronologique — le mode réservation instantanée
 * (`EtapeHoraire`) affiche la grille entière, complet compris.
 */
export function toutesLesHeures(jour) {
  return [
    ...jour.creneaux.map((iso) => ({ iso, complet: false })),
    ...jour.creneauxComplets.map((iso) => ({ iso, complet: true })),
  ].sort((a, b) => a.iso.localeCompare(b.iso));
}

/**
 * Les blocs d'une heure du mode « demande à confirmer », heures complètes
 * incluses (FIXERLIGHT-070). Un bloc sans aucun départ libre dans l'heure
 * porte `complet: true` et `iso: null` : rien à proposer à l'api pour ce
 * bloc, il n'est pas sélectionnable — distinct d'une heure hors horaires, qui
 * ne produit aucun bloc du tout (ni dans `creneauxLibres` ni dans
 * `creneauxComplets`).
 */
export function blocsHorairesAvecComplet(creneauxLibres, creneauxComplets) {
  const libresParHeure = new Map();
  (creneauxLibres || []).forEach((iso) => {
    const h = heureDuJour(iso);
    if (!libresParHeure.has(h)) libresParHeure.set(h, iso);
  });
  const heuresComplet = new Set(
    (creneauxComplets || []).map((iso) => heureDuJour(iso)),
  );
  const heures = new Set([...libresParHeure.keys(), ...heuresComplet]);
  return [...heures]
    .sort((a, b) => a - b)
    .map((h) => ({
      heure: h,
      iso: libresParHeure.get(h) || null,
      complet: !libresParHeure.has(h),
      libelle: `${String(h).padStart(2, '0')}h-${String(h + 1).padStart(
        2,
        '0',
      )}h`,
    }));
}

/** La première heure libre, ou `null`. */
export const plusTot = (slots) =>
  slots && slots.length
    ? [...slots].sort((a, b) => a.localeCompare(b))[0]
    : null;

/** `YYYY-MM-DD` à `decalage` jours d'aujourd'hui, à l'heure de Paris. */
export const jourDans = (decalage) =>
  jourSuivant(cleJour(new Date()), decalage);

/**
 * Le jour d'un créneau pour une étiquette courte : « Aujourd’hui », « Demain »,
 * sinon le jour de la semaine en toutes lettres (« Jeudi »).
 */
export function jourEtiquette(iso, maintenant = new Date()) {
  const ecart = ecartJours(iso, maintenant);
  if (ecart === 0) return 'Aujourd’hui';
  if (ecart === 1) return 'Demain';
  return majuscule(format('fr-FR', { weekday: 'long' }).format(date(iso)));
}

/** « 24 sept. ». */
export const dateCourte = (iso) =>
  format('fr-FR', { day: 'numeric', month: 'short' }).format(date(iso));
